import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Briefcase, Calendar, MapPin, Users, Plus, Search, 
  Loader2, CheckCircle2, Clock, AlertCircle, FolderOpen,
  ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkspaceListPage() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // For creating new workspace
  const [bookings, setBookings] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (user?.role !== 'admin' && user?.role !== 'staff') {
      navigate('/');
      return;
    }
    fetchWorkspaces();
  }, [token, user, navigate]);

  const fetchWorkspaces = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/workspaces?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkspaces(response.data.workspaces || []);
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingsForCreate = async () => {
    try {
      // Get paid bookings that don't have workspaces yet
      const response = await axios.get(`${API}/bookings/my-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter out bookings that already have workspaces
      const workspaceBookingIds = workspaces.map(w => w.booking_id);
      const availableBookings = (response.data.bookings || []).filter(
        b => !workspaceBookingIds.includes(b.booking_id)
      );
      setBookings(availableBookings);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const createWorkspace = async (bookingId) => {
    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/workspaces?booking_id=${bookingId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Navigate to the new workspace
      navigate(`/admin/workspaces/${response.data.workspace_id}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'completed':
        return <Badge className="bg-blue-500">Completed</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTaskProgress = (taskSummary) => {
    if (!taskSummary || Object.keys(taskSummary).length === 0) return null;
    const total = Object.values(taskSummary).reduce((a, b) => a + b, 0);
    const done = taskSummary.done || 0;
    return { done, total, percent: total > 0 ? Math.round((done / total) * 100) : 0 };
  };

  const filteredWorkspaces = workspaces.filter(ws => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ws.title?.toLowerCase().includes(query) ||
        ws.client_name?.toLowerCase().includes(query) ||
        ws.booking_id?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" data-testid="workspace-list-title">
              <Briefcase className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-500" />
              Event Workspaces
            </h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              Manage your event execution and vendor coordination
            </p>
          </div>
          <Button
            onClick={() => {
              fetchBookingsForCreate();
              setShowCreateDialog(true);
            }}
            data-testid="create-workspace-btn"
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            New Workspace
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-6">
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search workspaces..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-workspaces"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); fetchWorkspaces(); }}>
            <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Workspace Grid */}
        {filteredWorkspaces.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No workspaces yet</h3>
              <p className="text-muted-foreground mb-4">
                Create a workspace from a confirmed booking to start managing your event
              </p>
              <Button onClick={() => {
                fetchBookingsForCreate();
                setShowCreateDialog(true);
              }}>
                <Plus className="mr-2 h-4 w-4" />
                Create Workspace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWorkspaces.map(workspace => {
              const taskProgress = getTaskProgress(workspace.task_summary);
              const vendorAccepted = workspace.vendor_summary?.accepted || 0;
              const vendorTotal = Object.values(workspace.vendor_summary || {}).reduce((a, b) => a + b, 0);
              
              return (
                <Card 
                  key={workspace.workspace_id}
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/admin/workspaces/${workspace.workspace_id}`)}
                  data-testid={`workspace-card-${workspace.workspace_id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{workspace.title}</CardTitle>
                        <CardDescription>{workspace.client_name || 'No client'}</CardDescription>
                      </div>
                      {getStatusBadge(workspace.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Event Info */}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(workspace.event_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {workspace.guest_count}
                        </span>
                      </div>
                      
                      {workspace.event_location && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          {workspace.event_location}
                        </div>
                      )}

                      {/* Progress Indicators */}
                      <div className="pt-2 border-t space-y-2">
                        {taskProgress && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              Tasks
                            </span>
                            <span>{taskProgress.done}/{taskProgress.total} ({taskProgress.percent}%)</span>
                          </div>
                        )}
                        {vendorTotal > 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1">
                              <Users className="h-4 w-4 text-indigo-500" />
                              Vendors
                            </span>
                            <span>{vendorAccepted}/{vendorTotal} confirmed</span>
                          </div>
                        )}
                      </div>

                      {/* Services Count */}
                      <div className="flex items-center justify-between pt-2">
                        <span className="text-sm text-muted-foreground">
                          {workspace.service_items?.length || 0} services
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Workspace Dialog */}
        {showCreateDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-lg mx-4">
              <CardHeader>
                <CardTitle>Create New Workspace</CardTitle>
                <CardDescription>
                  Select a confirmed booking to create a workspace
                </CardDescription>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      No available bookings. All bookings already have workspaces.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {bookings.map(booking => (
                      <div
                        key={booking.booking_id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
                      >
                        <div>
                          <p className="font-medium">{booking.title || 'Event'}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(booking.event_date)} • {booking.guest_count} guests
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => createWorkspace(booking.booking_id)}
                          disabled={creating}
                        >
                          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-end mt-4">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
